import firebase from 'firebase/compat/app';

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectOption } from '../../components/Select';
import db_config from '../../configs/db_config';
import { IStadium } from '../../interfaces/types';
import { db } from '../../libs/firebase';
import { SeatInfoFormData } from '../../pages/SeatInfo/SeatInfoForm';
import { getItemWithExpiry, setItemWithExpiry } from '../../utils';
import { getJstDateKey } from '../../utils/jstDate';

export interface ISeatInfo {
  id: string;
  area: string;
  seat_type: string;
  aisle: string;
  block: string;
  row: string;
  seat_number: string;
  special_order: boolean;
  pre_order?: boolean;
  meny_order?: boolean;
  order_date: string;
  email: string;
  phone: string;
  isConfirmed?: boolean;
}

export interface SeatFormOptions {
  orderDateOptions: SelectOption[];
  seatTypeOptions: SelectOption[];
  areaOptions: SelectOption[];
  aisleOptions: SelectOption[];
  additionalInfoOptions: SelectOption[];
  rowOptions: SelectOption[];
  seatNumberOptions: SelectOption[];
}

// const AREAS = ['ネット裏', 'ライト側', 'レフト側', '１塁側', '２階ネット裏', '３塁側'];

export const useSeat = (stadium?: IStadium | null) => {
  const { t } = useTranslation();

  const [seatInfo, setSeatInfo] = useState<ISeatInfo | null>(null);
  const [seatFormOptions, setSeatFormOptions] = useState<SeatFormOptions>({
    orderDateOptions: [],
    seatTypeOptions: [],
    areaOptions: [],
    aisleOptions: [],
    additionalInfoOptions: [],
    rowOptions: [],
    seatNumberOptions: [],
  });
  const [isLoadingSeat, setIsLoadingSeat] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [allSeatsData, setAllSeatsData] = useState<any[]>([]);
  const [isSeatsDataCached, setIsSeatsDataCached] = useState<boolean>(false);

  const seat_doc = stadium?.cms_settings?.pre_order ? 'data_2026' : 'data';

  // Fetch all seats data once and cache it
  const fetchAllSeatsData = async (): Promise<void> => {
    if (!stadium?.id || isSeatsDataCached) {
      return;
    }

    setIsLoadingSeat(true);
    setError(null);

    try {
      const seatsRef = db.collection(db_config.DB_TABLE_STADIUM_MANAGE_SEATS).doc(stadium.id).collection(seat_doc);
      const querySnapshot = await seatsRef.get();
      const seatsData = querySnapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id }) as any);

      setAllSeatsData(seatsData);
      setIsSeatsDataCached(true);

      const seatTypes = seatsData.map((seat: any) => seat.seat_type).filter((type: any) => type);
      const uniqueSeatTypes = Array.from(new Set(seatTypes));
      setSeatFormOptions((prev) => ({
        ...prev,
        seatTypeOptions: uniqueSeatTypes.sort().map((type) => ({ value: type, label: type })),
      }));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch seats data';
      setError(errorMessage);
      console.error('Error fetching all seats data:', err);
    } finally {
      setIsLoadingSeat(false);
    }
  };

  const fetchSeatData = async (seatID: string) => {
    setIsLoadingSeat(true);
    setError(null);

    if (!seatID || !stadium?.id) {
      setIsLoadingSeat(false);
      return;
    }
    const cachedSeatsData = sessionStorage.getItem('seat-info');
    const cachedSeatId = sessionStorage.getItem('seatID');

    if (cachedSeatsData && cachedSeatId) {
      const seatInfo = JSON.parse(cachedSeatsData) as ISeatInfo;

      if (seatInfo?.id === seatID) {
        setSeatInfo(seatInfo);
        setIsLoadingSeat(false);
        return;
      }
    }

    try {
      const seatRef = db.collection(db_config.DB_TABLE_STADIUM_MANAGE_SEATS).doc(stadium.id).collection(seat_doc).doc(seatID);
      const seatDoc = await seatRef.get();

      if (!seatDoc.exists) {
        setError('Seat not found');
        setIsLoadingSeat(false);
        return;
      }

      const seatData = seatDoc.data();
      if (seatData) {
        setSeatInfo({
          id: seatDoc.id,
          area: seatData.area || '',
          seat_type: seatData.seat_type || '',
          aisle: seatData.aisle || '',
          block: seatData.block || '',
          row: seatData.row || '',
          seat_number: seatData.seat_number || '',
          order_date: seatData.order_date || '',
          email: '',
          phone: '',
          special_order: seatData.special_order || false,
          pre_order: seatData.pre_order || false,
          meny_order: seatData.meny_order || false,
          isConfirmed: sessionStorage.getItem('seatID') ? true : false,
        });
      } else {
        setError('No seat data found');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch seat info';
      setError(errorMessage);
      console.error('Error fetching seat info:', err);
    } finally {
      setIsLoadingSeat(false);
    }
  };

  const fetchAreasBySeatType = (seatType: string): void => {
    if (!seatType) {
      setSeatFormOptions((prev) => ({
        ...prev,
        areaOptions: [],
        aisleOptions: [],
        additionalInfoOptions: [],
        rowOptions: [],
        seatNumberOptions: [],
      }));
      return;
    }

    const filteredSeats = allSeatsData.filter((seat) => seat.seat_type === seatType);
    const areas = filteredSeats.map((seat) => seat.area).filter((area) => area || area === '');
    const uniqueAreas = Array.from(new Set(areas));

    const areaOptions = [
      { value: 'NULL', label: React.createElement('span', { className: 'font-bold' }, t('UNKNOWN')) },
      ...uniqueAreas
        .filter((area) => area)
        .sort()
        .map((area) => ({
          value: area,
          label: area,
        })),
    ];

    setSeatFormOptions((prev) => ({
      ...prev,
      areaOptions,
      aisleOptions: [],
      additionalInfoOptions: [],
      rowOptions: [],
      seatNumberOptions: [],
    }));
  };

  const fetchAislesBySeatTypeAndArea = (seatType: string, area: string): void => {
    if (!seatType || area === undefined) {
      setSeatFormOptions((prev) => ({
        ...prev,
        aisleOptions: [],
        additionalInfoOptions: [],
        rowOptions: [],
        seatNumberOptions: [],
      }));
      return;
    }

    // Handle NULL value - skip area check when area is NULL
    const filteredSeats =
      area === 'NULL' ? allSeatsData.filter((seat) => seat.seat_type === seatType) : allSeatsData.filter((seat) => seat.seat_type === seatType && seat.area === area);
    const aisles = filteredSeats.map((seat) => seat.aisle).filter((aisle) => aisle || aisle === '');
    const uniqueAisles = Array.from(new Set(aisles));

    setSeatFormOptions((prev) => ({
      ...prev,
      aisleOptions: uniqueAisles.sort().map((aisle) => ({
        value: aisle || '',
        label: aisle || t('EMPTY'),
      })),
      additionalInfoOptions: [],
      rowOptions: [],
      seatNumberOptions: [],
    }));
  };

  const fetchAdditionalBySeatTypeAreaAndAisle = (seatType: string, area: string, aisle: string): void => {
    if (!seatType || area === undefined || aisle === undefined) {
      setSeatFormOptions((prev) => ({
        ...prev,
        additionalInfoOptions: [],
        rowOptions: [],
        seatNumberOptions: [],
      }));
      return;
    }

    // Handle NULL value - skip area check when area is NULL
    const filteredSeats = allSeatsData.filter((seat) => {
      const areaMatch = area === 'NULL' ? true : seat.area === area;
      return seat.seat_type === seatType && areaMatch && seat.aisle === aisle;
    });
    const blocks = filteredSeats.map((seat) => seat.block).filter((block) => block || block === '');
    const uniqueBlocks = Array.from(new Set(blocks));

    setSeatFormOptions((prev) => ({
      ...prev,
      additionalInfoOptions: uniqueBlocks.sort().map((block) => ({
        value: block || '',
        label: block || t('EMPTY'),
      })),
      rowOptions: [],
      seatNumberOptions: [],
    }));
  };

  const fetchRowsBySeatTypeAreaAisleAndAdditional = (seatType: string, area: string, aisle: string, additional: string): void => {
    if (!seatType || area === undefined || aisle === undefined || additional === undefined) {
      setSeatFormOptions((prev) => ({
        ...prev,
        rowOptions: [],
        seatNumberOptions: [],
      }));
      return;
    }

    // Handle NULL value - skip area check when area is NULL
    const filteredSeats = allSeatsData.filter((seat) => {
      const areaMatch = area === 'NULL' ? true : seat.area === area;
      return seat.seat_type === seatType && areaMatch && seat.aisle === aisle && seat.block === additional;
    });

    const rows = filteredSeats
      .map((seat) => seat.row)
      .filter((row) => row)
      .sort((a, b) => {
        const numA = parseInt(a);
        const numB = parseInt(b);
        if (!isNaN(numA) && !isNaN(numB)) {
          return numA - numB;
        }
        return a.localeCompare(b);
      });

    const uniqueRows = Array.from(new Set(rows));

    setSeatFormOptions((prev) => ({
      ...prev,
      rowOptions: uniqueRows.map((row) => ({ value: row, label: row })),
      seatNumberOptions: [],
    }));
  };

  const fetchSeatNumbersBySeatTypeAreaAisleAdditionalAndRow = (seatType: string, area: string, aisle: string, additional: string, row: string): void => {
    if (!seatType || area === undefined || aisle === undefined || additional === undefined || !row) {
      setSeatFormOptions((prev) => ({
        ...prev,
        seatNumberOptions: [],
      }));
      return;
    }

    // Handle NULL value - skip area check when area is NULL
    const filteredSeats = allSeatsData.filter((seat) => {
      const areaMatch = area === 'NULL' ? true : seat.area === area;
      return seat.seat_type === seatType && areaMatch && seat.aisle === aisle && seat.block === additional && seat.row === row;
    });

    const seatNumbers = filteredSeats
      .map((seat) => seat.seat_number)
      .filter((num) => num)
      .sort((a, b) => {
        const numA = parseInt(a);
        const numB = parseInt(b);
        if (!isNaN(numA) && !isNaN(numB)) {
          return numA - numB;
        }
        return a.localeCompare(b);
      });

    const uniqueSeatNumbers = Array.from(new Set(seatNumbers));

    setSeatFormOptions((prev) => ({
      ...prev,
      seatNumberOptions: uniqueSeatNumbers.map((num) => ({ value: num, label: num })),
    }));
  };

  const updateSeatInfo = async (formData: SeatInfoFormData, isPreOrder: boolean): Promise<{ pre_order: boolean }> => {
    setIsLoadingSeat(true);
    setError(null);

    if (!stadium?.id) {
      setError('Stadium ID is required');
      setIsLoadingSeat(false);
      throw new Error('Stadium ID is required');
    }

    try {
      // Fetch seat data from 
      const seatsRef = db.collection(db_config.DB_TABLE_STADIUM_MANAGE_SEATS).doc(stadium.id).collection(seat_doc);

      let query = seatsRef
        .where('seat_type', '==', formData.seat_type)
        .where('aisle', '==', formData.aisle)
        .where('block', '==', formData.block)
        .where('row', '==', formData.row)
        .where('seat_number', '==', formData.seat_number);

      if (formData.area !== 'NULL') {
        query = query.where('area', '==', formData.area);
      }

      const querySnapshot = await query.get();

      if (querySnapshot.empty) {
        setError('CAN_NOT_FIND_SEAT');
        setIsLoadingSeat(false);
        throw new Error('CAN_NOT_FIND_SEAT');
      }

      const seatDoc = querySnapshot.docs[0];
      const matchedSeat = { ...seatDoc.data(), id: seatDoc.id } as ISeatInfo;

      if (matchedSeat.meny_order === false) {
        setError('THIS_SEAT_CAN_NOT_ACCESS_MENY');
        setIsLoadingSeat(false);
        throw new Error('THIS_SEAT_CAN_NOT_ACCESS_MENY');
      }

      let gameDayPreorder = true;
      try {
        const dateKey = getJstDateKey(formData.order_date);
        const gameDayRef = db
          .collection(db_config.DB_TABLE_STADIUMS_GAMEDAYS)
          .doc(stadium!.id)
          .collection('gamedays')
          .where('date', '==', dateKey);
        const gameDaySnap = await gameDayRef.get();
        if (!gameDaySnap.empty) {
          const gameDayData = gameDaySnap.docs[0].data();
          gameDayPreorder = gameDayData.preorder !== false;
        }
      } catch (e) {
        console.warn('[PreOrder Check] Failed to fetch gameday, defaulting preorder=true', e);
      }
      const effectiveSeatPreOrder = gameDayPreorder && (matchedSeat.pre_order !== false);

      if (isPreOrder && !effectiveSeatPreOrder) {
        setError('THIS_SEAT_CAN_NOT_PRE_ORDER');
        setIsLoadingSeat(false);
        throw new Error('THIS_SEAT_CAN_NOT_PRE_ORDER');
      }

      const newSeatInfo = {
        id: matchedSeat.id,
        area: matchedSeat.area || '',
        seat_type: matchedSeat.seat_type || '',
        aisle: matchedSeat.aisle || '',
        block: matchedSeat.block || '',
        row: matchedSeat.row || '',
        seat_number: matchedSeat.seat_number || '',
        order_date: formData.order_date,
        special_order: matchedSeat.special_order || false,
        pre_order: effectiveSeatPreOrder,
        meny_order: matchedSeat.meny_order || false,
        email: formData.email,
        phone: formData.phone,
        isConfirmed: false,
      };

      setSeatInfo(newSeatInfo);

      const retentionDays = stadium?.email_retention_days ?? 30;
      if (formData.email) setItemWithExpiry('user_email', formData.email, retentionDays);
      if (formData.phone) setItemWithExpiry('user_phone', formData.phone, retentionDays);

      return { pre_order: effectiveSeatPreOrder };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'ERROR_SELECTING_SEAT';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoadingSeat(false);
    }
  };

  const confirmSeatInfo = async (isConfirmed: boolean = true) => {
    if (seatInfo) {
      setSeatInfo({ ...seatInfo, isConfirmed });
      sessionStorage.setItem('seatID', seatInfo.id);
      sessionStorage.setItem('seat-info', JSON.stringify({ ...seatInfo, isConfirmed }));
      const retentionDays = stadium?.email_retention_days ?? 30;

      if (seatInfo.email && stadium?.id) {
        const email = seatInfo.email.trim().toLowerCase();
        const phone = seatInfo.phone ? seatInfo.phone.replace(/-/g, '').trim() : '';

        if (email) {
          const storedEmail = getItemWithExpiry('user_email');
          if (storedEmail !== email) {
            setItemWithExpiry('user_email', email, retentionDays);
          }
        }

        if (phone) {
          const storedPhone = getItemWithExpiry('user_phone');
          if (storedPhone !== phone) {
            setItemWithExpiry('user_phone', phone, retentionDays);
          }
        }

        try {
          const docId = `${seatInfo.id}_${email}`;

          const docRef = db.collection(db_config.DB_TABLE_USER_EMAILS).doc(docId);
          const docSnapshot = await docRef.get();

          let shouldSave = false;

          if (!docSnapshot.exists) {
            shouldSave = true;
          } else {
            const existingData = docSnapshot.data();
            const existingExpiresAt = new Date((existingData?.expires_at as firebase.firestore.Timestamp).seconds * 1000);

            if (existingExpiresAt < new Date()) {
              shouldSave = true;
            }
          }

          if (shouldSave) {
            const expiresAt = new Date();
            expiresAt.setDate(expiresAt.getDate() + retentionDays);

            await docRef.set({
              expires_at: firebase.firestore.Timestamp.fromDate(expiresAt),
              email,
              phone,
              seat_id: seatInfo.id,
            });
          }
        } catch (error) {
          console.error('Error saving email to Firestore:', error);
        }
      }
    }
  };

  const setSeatFormOrderDateOptions = (orderDateOptions: SelectOption[]) => {
    setSeatFormOptions((prev) => ({
      ...prev,
      orderDateOptions,
    }));
  };

  const getSeatById = (id?: string) => {
    if (!id) return null;
    return allSeatsData.find((s) => s.id === id) || null;
  };

  const formatSeatInfo = (seat: any) => {
    if (!seat) return '';
    const parts = [seat.area, seat.seat_type, seat.block, seat.row, seat.seat_number];
    return parts.filter(Boolean).join('・');
  };

  const getFormattedSeat = (idOrSeat?: string | any) => {
    if (!idOrSeat) return '';
    if (typeof idOrSeat === 'string') {
      const found = getSeatById(idOrSeat);
      return formatSeatInfo(found);
    }
    return formatSeatInfo(idOrSeat);
  };

  return {
    seatInfo,
    setSeatInfo,
    seatFormOptions,
    setSeatFormOrderDateOptions,
    isLoadingSeat,
    error: error,
    fetchSeatData,
    fetchAllSeatsData,
    fetchAreasBySeatType,
    fetchAislesBySeatTypeAndArea,
    fetchAdditionalBySeatTypeAreaAndAisle,
    fetchRowsBySeatTypeAreaAisleAndAdditional,
    fetchSeatNumbersBySeatTypeAreaAisleAdditionalAndRow,
    updateSeatInfo,
    confirmSeatInfo,
    allSeatsData,
    getSeatById,
    formatSeatInfo,
    getFormattedSeat,
  };
};
